import { Check, Close, ContentCopy } from "@mui/icons-material";
import {
    Box,
    IconButton,
    InputAdornment,
    outlinedInputClasses,
    Snackbar,
    snackbarContentClasses,
    Typography,
} from "@mui/material";
import { memo, useCallback, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { TextField } from "components";
import { explorerActions, selectSnackbarMessage } from "slices/explorer";

export default memo(function GlobalSnackbar() {
    const message = useAppSelector(selectSnackbarMessage);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const copied = message?.key === copiedKey;

    const dispatch = useAppDispatch();

    const onClose = useCallback(() => {
        dispatch(explorerActions.setSnackbarMessage(null));
    }, [dispatch]);

    if (message === null) {
        return null;
    }

    const handleCopyLink = async () => {
        if (!message.link) {
            return;
        }
        try {
            await navigator.clipboard.writeText(message.link);
            setCopiedKey(message.key);
        } catch {
            // the link is visible for manual copy
        }
    };

    return (
        <Snackbar
            key={message.key}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{
                width: { xs: "auto", sm: 350 },
                bottom: { xs: "auto", sm: 88 },
                top: { xs: 24, sm: "auto" },

                [`& .${snackbarContentClasses.root}`]: {
                    pointerEvents: "auto",
                    flexWrap: "nowrap",
                    alignItems: "flex-start",
                },

                [`& .${snackbarContentClasses.action}`]: {
                    mt: 0.5,
                },
            }}
            autoHideDuration={message.link ? null : (message.closeAfter ?? 2500)}
            open={true}
            onClose={onClose}
            message={
                message.link ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                        <Typography variant="body2">{message.msg}</Typography>
                        <TextField
                            value={message.link}
                            size="small"
                            fullWidth
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton color="inherit" size="small" onClick={handleCopyLink} edge="end">
                                            {copied ? (
                                                <Check htmlColor="white" fontSize="small" />
                                            ) : (
                                                <ContentCopy htmlColor="white" fontSize="small" />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            onFocus={(e) => e.target.select()}
                            sx={{
                                mt: 1,
                                [`& .${outlinedInputClasses.root}`]: {
                                    color: "common.white",
                                },
                            }}
                        />
                    </Box>
                ) : (
                    message.msg
                )
            }
            action={
                <IconButton size="small" aria-label="close" color="inherit" onClick={onClose}>
                    <Close fontSize="small" />
                </IconButton>
            }
        />
    );
});
